import {
  Box3,
  BufferAttribute,
  BufferGeometry,
  Color,
  DoubleSide,
  InstancedBufferAttribute,
  InstancedBufferGeometry,
  Mesh,
  MirroredRepeatWrapping,
  Object3D,
  PerspectiveCamera,
  RawShaderMaterial,
  RepeatWrapping,
  RGBFormat,
  Scene,
  ShaderMaterial,
  Texture,
  TextureLoader,
  UnsignedByteType,
  Vector3,
  WebGLRenderer,
  WebGLRenderTarget,
} from "three";
import grassColorSrc from "url:./grass.jpg";
import noiseSrc from "url:../../maps/noise-clouds.png";
import fragmentShader from "./shader.frag";
import vertexShader from "./shader.vert";
import Easing from "easing-functions";
import HeightMapShader from "../../shaders/HeightMapShader/HeightMapShader";

export const BLADE_SEGS = 5; // # of blade segments
export const BLADE_WIDTH = 0.06;
export const BLADE_HEIGHT_MIN = 0.25;
export const BLADE_HEIGHT_MAX = 0.75;

export const FOG_COLOR = new Color(1.0, 0.65, 0.36);
export const GRASS_FOG_COLOR = new Color(0.8, 0.4, 0.07);

export const HEIGHT_MAP_SCALE = [0.00005, 0.000125, 280.0];

interface Options {
  density?: number;
  geometry: BufferGeometry;
  renderer: WebGLRenderer;
  camera: PerspectiveCamera;
}

// Texture to pixel data (to place grass only where there is grass)
// https://discourse.threejs.org/t/solved-extract-pixels-data-from-datatexture/6339/3

class GPUGrass extends Object3D {
  private time = 0;
  private bladeCount: number;
  private mesh: Mesh;
  private geometry = new InstancedBufferGeometry();
  private material: ShaderMaterial;
  private srcGeometry: BufferGeometry;
  private density: number;
  private renderer: WebGLRenderer;
  private camera: PerspectiveCamera;

  constructor({ density = 10, geometry, renderer, camera }: Options) {
    super();
    this.camera = camera;
    this.renderer = renderer;
    this.srcGeometry = geometry;
    this.density = density;
  }

  public async init() {
    const { heightMap, isGrassyAtPosition, landscapeAabb } =
      await this.generateHeightMap();

    const tmp1 = new Vector3().copy(landscapeAabb.min);
    tmp1.y = 0;
    const tmp2 = new Vector3().copy(landscapeAabb.max);
    tmp2.y = 0;

    const patchSize = landscapeAabb.max.x - landscapeAabb.min.x;
    const patchRadius = patchSize * 0.5;

    const maxBladeCount = Math.round(patchSize * patchSize) * this.density;

    // building an array of positions first allows us to know how many blades we have
    // once we've discarded none-grassy areas, so we can set our buffer lenghts.
    const positions = [];

    for (let i = 0; i < maxBladeCount; i++) {
      const dist = Math.random();
      const angle = Math.random() * Math.PI * 2;
      const x = dist * Math.cos(angle);
      const z = dist * Math.sin(angle);

      const isGrassy = isGrassyAtPosition((x + 1) / 2, (z + 1) / 2);
      if (isGrassy) {
        positions.push({ x: x * patchRadius, z: z * patchRadius });
      }
    }

    this.bladeCount = positions.length;
    this.geometry.instanceCount = this.bladeCount;

    const bladeIndices = BLADE_SEGS * 12;
    const indices = new Uint16Array(bladeIndices);
    let indicesIndex = 0;
    let vertexOffsetFront = 0;
    let vertexOffsetBack = (BLADE_SEGS + 1) * 2;

    for (let i = 0; i < BLADE_SEGS; ++i) {
      indices[indicesIndex++] = vertexOffsetFront + 0;
      indices[indicesIndex++] = vertexOffsetFront + 1;
      indices[indicesIndex++] = vertexOffsetFront + 2;
      indices[indicesIndex++] = vertexOffsetFront + 2;
      indices[indicesIndex++] = vertexOffsetFront + 1;
      indices[indicesIndex++] = vertexOffsetFront + 3;
      vertexOffsetFront += 2;
    }

    for (let i = 0; i < BLADE_SEGS; ++i) {
      indices[indicesIndex++] = vertexOffsetBack + 2;
      indices[indicesIndex++] = vertexOffsetBack + 1;
      indices[indicesIndex++] = vertexOffsetBack + 0;
      indices[indicesIndex++] = vertexOffsetBack + 3;
      indices[indicesIndex++] = vertexOffsetBack + 1;
      indices[indicesIndex++] = vertexOffsetBack + 2;
      vertexOffsetBack += 2;
    }

    this.geometry.setIndex(new BufferAttribute(indices, 1));

    const shapes = new Float32Array(this.bladeCount * 4);
    for (let i = 0; i < this.bladeCount; i++) {
      shapes[i * 4 + 0] = BLADE_WIDTH / 2 + Math.random() * (BLADE_WIDTH / 2); // width;
      shapes[i * 4 + 1] =
        BLADE_HEIGHT_MIN +
        Easing.Cubic.In(Math.random()) * (BLADE_HEIGHT_MAX - BLADE_HEIGHT_MIN); // height;
      shapes[i * 4 + 2] = 0.07 + Math.random() * 0.15; // lean (static);
      shapes[i * 4 + 3] = 0.05 + Math.random() * 0.02; // curve (animated)
    }

    this.geometry.setAttribute(
      "shape",
      new InstancedBufferAttribute(shapes, 4)
    );

    const offsets = new Float32Array(4 * this.bladeCount);

    for (let i = 0; i < this.bladeCount; i++) {
      const { x, z } = positions[i];

      offsets[i * 4 + 0] = x - patchRadius; // x
      offsets[i * 4 + 1] = 0; // y
      offsets[i * 4 + 2] = z - patchRadius; // z
      offsets[i * 4 + 3] = Math.PI * 2.0 * Math.random(); // rot
    }

    this.geometry.setAttribute(
      "offset",
      new InstancedBufferAttribute(offsets, 4)
    );

    const indexVerts = new Float32Array((BLADE_SEGS + 1) * 4);
    for (let i = 0; i < indexVerts.length; ++i) {
      indexVerts[i] = i;
    }
    this.geometry.setAttribute("vindex", new BufferAttribute(indexVerts, 1));

    this.geometry.rotateX(Math.PI * 0.5);

    const textureLoader = new TextureLoader();
    const tSurfaceColor = textureLoader.load(grassColorSrc);
    tSurfaceColor.wrapS = tSurfaceColor.wrapT = RepeatWrapping;

    const tNoise = textureLoader.load(noiseSrc);
    tNoise.wrapS = tNoise.wrapT = MirroredRepeatWrapping;

    this.material = new RawShaderMaterial({
      // transparent: true,
      uniforms: {
        uTime: {
          value: 0,
        },
        uBladeSegs: {
          value: BLADE_SEGS,
        },
        tSurfaceColor: {
          value: tSurfaceColor,
        },
        tNoise: {
          value: tNoise,
        },
        uPatchSize: {
          value: patchSize,
        },
        uDrawPos: { value: [0.0, 0.0] },
        uHeightMap: { value: heightMap },
        uAabbMin: { value: landscapeAabb.min },
        uAabbMax: { value: landscapeAabb.max },
      },
      depthWrite: false,
      depthTest: true,
      vertexShader,
      fragmentShader,
      side: DoubleSide,
    });

    this.mesh = new Mesh(this.geometry, this.material);
    this.mesh.frustumCulled = false;
    this.add(this.mesh);
  }

  private async generateHeightMap(): Promise<{
    heightMap: Texture;
    isGrassyAtPosition: (x: number, y: number) => boolean;
    landscapeAabb: Box3;
  }> {
    const TEX_SIZE = 1024 * 2;

    this.srcGeometry.computeBoundingBox();
    const heightScene = new Scene();
    const heightMaterial = new RawShaderMaterial(HeightMapShader);
    heightMaterial.wireframe = false;
    heightMaterial.uniforms.uBoxMin.value = this.srcGeometry.boundingBox.min;
    heightMaterial.uniforms.uBoxMax.value = this.srcGeometry.boundingBox.max;
    const heightMesh = new Mesh(this.srcGeometry, heightMaterial);
    heightMesh.frustumCulled = false;
    heightScene.add(heightMesh);

    const renderTarget = new WebGLRenderTarget(TEX_SIZE, TEX_SIZE);
    // renderTarget.texture.flipY = false;
    const pixels = new Uint8Array(TEX_SIZE * TEX_SIZE * 4);

    const clearColour = this.renderer.getClearColor(new Color());
    const clearAlpha = this.renderer.getClearAlpha();
    this.renderer.setClearColor(0x000000);
    this.renderer.setRenderTarget(renderTarget);
    this.renderer.render(heightScene, this.camera);
    this.renderer.readRenderTargetPixels(
      renderTarget,
      0,
      0,
      TEX_SIZE,
      TEX_SIZE,
      pixels
    );
    this.renderer.setRenderTarget(null);
    this.renderer.setClearColor(clearColour);
    this.renderer.setClearAlpha(clearAlpha);

    return {
      heightMap: renderTarget.texture,
      // x and y are 0 > 1 fraction of width and height
      isGrassyAtPosition: (x, y) => {
        // return true;
        const _x = Math.round(x * TEX_SIZE);
        const _y = Math.round(y * TEX_SIZE);

        const arrayIndex = _y * TEX_SIZE * 4 + _x * 4;
        const r = pixels[arrayIndex];
        const g = pixels[arrayIndex + 1];
        const b = pixels[arrayIndex + 2];

        return (r + g + b) / 3 > 0;
      },
      landscapeAabb: this.srcGeometry.boundingBox,
    };
  }

  public update(delta) {
    this.time += 16.666 * delta;
    this.material.uniforms.uTime.value = this.time;
  }
}

export default GPUGrass;
